/**
 * FASE 3 - Report & Analytics
 * Confronti YoY, breakdown per canale, grafici
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BarChart3, TrendingUp, TrendingDown, Download, RefreshCw,
  Calendar, Euro, Building, ArrowUpRight, ArrowDownRight,
  PieChart, Activity, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
  'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'
];

const CHANNEL_COLORS = {
  'booking_com': { bg: 'bg-blue-500', text: 'text-blue-600', hex: '#3B82F6' },
  'airbnb': { bg: 'bg-red-500', text: 'text-red-600', hex: '#EF4444' },
  'expedia': { bg: 'bg-yellow-500', text: 'text-yellow-600', hex: '#EAB308' },
  'vrbo': { bg: 'bg-purple-500', text: 'text-purple-600', hex: '#A855F7' },
  'diretta': { bg: 'bg-green-500', text: 'text-green-600', hex: '#22C55E' },
  'altro': { bg: 'bg-gray-500', text: 'text-gray-600', hex: '#6B7280' }
};

export default function EconomicReports() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [compareYear, setCompareYear] = useState(new Date().getFullYear() - 1);
  const [reportData, setReportData] = useState(null);

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2,
    new Date().getFullYear() - 3
  ];

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch data for both years
      const [currentYearData, compareYearData] = await Promise.all([
        fetchYearData(selectedYear),
        fetchYearData(compareYear)
      ]);
      
      setReportData({
        currentYear: currentYearData,
        compareYear: compareYearData,
        yoyComparison: calculateYoY(currentYearData, compareYearData)
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, compareYear]);

  const fetchYearData = async (year) => {
    try {
      // Fetch monthly data for the year
      const monthlyPromises = [];
      for (let month = 1; month <= 12; month++) {
        monthlyPromises.push(
          axios.get(`${API}/admin/economic/kpi/${year}/${month}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: { entrate_lorde: 0, entrate_nette: 0, uscite: 0, utile_netto: 0 } }))
        );
      }
      
      const monthlyResults = await Promise.all(monthlyPromises);
      const monthlyData = monthlyResults.map(r => r.data);
      
      // Calculate annual totals
      const totals = monthlyData.reduce((acc, m) => ({
        entrate_lorde: acc.entrate_lorde + (m.entrate_lorde || 0),
        entrate_nette: acc.entrate_nette + (m.entrate_nette || 0),
        commissioni_ota: acc.commissioni_ota + (m.commissioni_ota || 0),
        uscite: acc.uscite + (m.uscite || 0),
        utile_netto: acc.utile_netto + (m.utile_netto || 0)
      }), { entrate_lorde: 0, entrate_nette: 0, commissioni_ota: 0, uscite: 0, utile_netto: 0 });

      // Fetch channel breakdown
      const channelData = await fetchChannelBreakdown(year);
      
      return {
        year,
        monthly: monthlyData,
        totals,
        channels: channelData
      };
    } catch (error) {
      console.error(`Error fetching data for ${year}:`, error);
      return {
        year,
        monthly: Array(12).fill({ entrate_lorde: 0, entrate_nette: 0, uscite: 0, utile_netto: 0 }),
        totals: { entrate_lorde: 0, entrate_nette: 0, commissioni_ota: 0, uscite: 0, utile_netto: 0 },
        channels: []
      };
    }
  };

  const fetchChannelBreakdown = async (year) => {
    try {
      const response = await axios.get(`${API}/admin/economic/incomes?anno=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const incomes = response.data.incomes || [];
      const channelTotals = {};
      
      incomes.forEach(income => {
        const channel = income.canale || 'altro';
        if (!channelTotals[channel]) {
          channelTotals[channel] = { lordo: 0, netto: 0, count: 0 };
        }
        channelTotals[channel].lordo += income.importo_lordo || 0;
        channelTotals[channel].netto += income.importo_netto || 0;
        channelTotals[channel].count += 1;
      });
      
      return Object.entries(channelTotals).map(([channel, data]) => ({
        channel,
        ...data
      })).sort((a, b) => b.netto - a.netto);
    } catch (error) {
      return [];
    }
  };

  const calculateYoY = (current, compare) => {
    const calc = (curr, comp) => {
      if (comp === 0) return curr > 0 ? 100 : 0;
      return ((curr - comp) / comp * 100);
    };
    
    return {
      entrate_lorde: calc(current.totals.entrate_lorde, compare.totals.entrate_lorde),
      entrate_nette: calc(current.totals.entrate_nette, compare.totals.entrate_nette),
      uscite: calc(current.totals.uscite, compare.totals.uscite),
      utile_netto: calc(current.totals.utile_netto, compare.totals.utile_netto)
    };
  };

  useEffect(() => {
    if (token) {
      fetchReportData();
    }
  }, [token, fetchReportData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getMaxValue = (data, key) => {
    return Math.max(...data.map(d => d[key] || 0), 1);
  };

  const exportCSV = () => {
    if (!reportData) return;
    
    const headers = ['Mese', `Entrate ${selectedYear}`, `Entrate ${compareYear}`, `Uscite ${selectedYear}`, `Uscite ${compareYear}`, `Utile ${selectedYear}`, `Utile ${compareYear}`];
    const rows = MONTHS.map((month, idx) => [
      month,
      reportData.currentYear.monthly[idx]?.entrate_nette || 0,
      reportData.compareYear.monthly[idx]?.entrate_nette || 0,
      reportData.currentYear.monthly[idx]?.uscite || 0,
      reportData.compareYear.monthly[idx]?.uscite || 0,
      reportData.currentYear.monthly[idx]?.utile_netto || 0,
      reportData.compareYear.monthly[idx]?.utile_netto || 0
    ]);
    
    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `report_${selectedYear}_vs_${compareYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast.success('Report esportato');
  };

  return (
    <AdminLayout title="Report & Analytics">
      <div className="space-y-4 pb-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-xl md:text-2xl text-[#1A202C]">Report & Analytics</h1>
            <p className="text-sm text-gray-500">Analisi comparativa e trend</p>
          </div>
          
          {/* Year Selectors */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
              <span className="text-sm text-gray-500">Anno:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border-0 bg-transparent font-medium"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <span className="text-gray-400">vs</span>
            <div className="flex items-center gap-2 bg-white rounded-lg border px-3 py-2">
              <select
                value={compareYear}
                onChange={(e) => setCompareYear(parseInt(e.target.value))}
                className="border-0 bg-transparent text-gray-600"
              >
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={exportCSV}>
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A059]"></div>
          </div>
        ) : reportData && (
          <>
            {/* YoY Comparison Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="border-t-4 border-t-blue-500">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Entrate Lorde</p>
                      <p className="text-xl font-bold text-gray-800">{formatCurrency(reportData.currentYear.totals.entrate_lorde)}</p>
                      <p className="text-sm text-gray-500">vs {formatCurrency(reportData.compareYear.totals.entrate_lorde)}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${reportData.yoyComparison.entrate_lorde >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {reportData.yoyComparison.entrate_lorde >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      <span className="font-bold">{formatPercent(reportData.yoyComparison.entrate_lorde)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-emerald-500">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Entrate Nette</p>
                      <p className="text-xl font-bold text-emerald-600">{formatCurrency(reportData.currentYear.totals.entrate_nette)}</p>
                      <p className="text-sm text-gray-500">vs {formatCurrency(reportData.compareYear.totals.entrate_nette)}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${reportData.yoyComparison.entrate_nette >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {reportData.yoyComparison.entrate_nette >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      <span className="font-bold">{formatPercent(reportData.yoyComparison.entrate_nette)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-red-500">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Uscite</p>
                      <p className="text-xl font-bold text-red-600">{formatCurrency(reportData.currentYear.totals.uscite)}</p>
                      <p className="text-sm text-gray-500">vs {formatCurrency(reportData.compareYear.totals.uscite)}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${reportData.yoyComparison.uscite <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {reportData.yoyComparison.uscite <= 0 ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
                      <span className="font-bold">{formatPercent(reportData.yoyComparison.uscite)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-t-4 border-t-amber-500">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Utile Netto</p>
                      <p className="text-xl font-bold text-amber-600">{formatCurrency(reportData.currentYear.totals.utile_netto)}</p>
                      <p className="text-sm text-gray-500">vs {formatCurrency(reportData.compareYear.totals.utile_netto)}</p>
                    </div>
                    <div className={`flex items-center gap-1 ${reportData.yoyComparison.utile_netto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {reportData.yoyComparison.utile_netto >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      <span className="font-bold">{formatPercent(reportData.yoyComparison.utile_netto)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Monthly Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                    Andamento Mensile Entrate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-end gap-1">
                    {MONTHS.map((month, idx) => {
                      const currentValue = reportData.currentYear.monthly[idx]?.entrate_nette || 0;
                      const compareValue = reportData.compareYear.monthly[idx]?.entrate_nette || 0;
                      const maxVal = getMaxValue([...reportData.currentYear.monthly, ...reportData.compareYear.monthly], 'entrate_nette');
                      const currentHeight = (currentValue / maxVal * 100);
                      const compareHeight = (compareValue / maxVal * 100);
                      
                      return (
                        <div key={month} className="flex-1 flex flex-col items-center gap-1">
                          <div className="flex gap-0.5 items-end h-48 w-full">
                            <div 
                              className="flex-1 bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                              style={{ height: `${Math.max(currentHeight, 2)}%` }}
                              title={`${selectedYear}: ${formatCurrency(currentValue)}`}
                            />
                            <div 
                              className="flex-1 bg-gray-300 rounded-t transition-all hover:bg-gray-400"
                              style={{ height: `${Math.max(compareHeight, 2)}%` }}
                              title={`${compareYear}: ${formatCurrency(compareValue)}`}
                            />
                          </div>
                          <span className="text-[10px] text-gray-500">{month}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center gap-4 mt-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-500 rounded"></div>
                      <span className="text-xs text-gray-600">{selectedYear}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-gray-300 rounded"></div>
                      <span className="text-xs text-gray-600">{compareYear}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Channel Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChart className="w-4 h-4 text-purple-500" />
                    Breakdown per Canale ({selectedYear})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reportData.currentYear.channels.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <PieChart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Nessun dato disponibile</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reportData.currentYear.channels.map((ch) => {
                        const channelConfig = CHANNEL_COLORS[ch.channel] || CHANNEL_COLORS['altro'];
                        const totalNetto = reportData.currentYear.channels.reduce((sum, c) => sum + c.netto, 0);
                        const percentage = totalNetto > 0 ? (ch.netto / totalNetto * 100) : 0;
                        
                        return (
                          <div key={ch.channel} className="space-y-1">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded ${channelConfig.bg}`}></div>
                                <span className="text-sm font-medium capitalize">
                                  {ch.channel.replace('_', ' ')}
                                </span>
                                <span className="text-xs text-gray-400">({ch.count} pren.)</span>
                              </div>
                              <div className="text-right">
                                <span className={`font-bold ${channelConfig.text}`}>{formatCurrency(ch.netto)}</span>
                                <span className="text-xs text-gray-400 ml-2">({percentage.toFixed(1)}%)</span>
                              </div>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${channelConfig.bg} rounded-full transition-all`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Monthly Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-500" />
                  Dettaglio Mensile Comparativo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-2 font-medium text-gray-600">Mese</th>
                        <th className="text-right p-2 font-medium text-blue-600">Entrate {selectedYear}</th>
                        <th className="text-right p-2 font-medium text-gray-400">Entrate {compareYear}</th>
                        <th className="text-center p-2 font-medium text-gray-600">Var %</th>
                        <th className="text-right p-2 font-medium text-red-600">Uscite {selectedYear}</th>
                        <th className="text-right p-2 font-medium text-gray-400">Uscite {compareYear}</th>
                        <th className="text-right p-2 font-medium text-amber-600">Utile {selectedYear}</th>
                        <th className="text-right p-2 font-medium text-gray-400">Utile {compareYear}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {MONTHS.map((month, idx) => {
                        const curr = reportData.currentYear.monthly[idx] || {};
                        const comp = reportData.compareYear.monthly[idx] || {};
                        const entrateVar = comp.entrate_nette > 0 
                          ? ((curr.entrate_nette - comp.entrate_nette) / comp.entrate_nette * 100) 
                          : (curr.entrate_nette > 0 ? 100 : 0);
                        
                        return (
                          <tr key={month} className="border-b hover:bg-gray-50">
                            <td className="p-2 font-medium">{month}</td>
                            <td className="p-2 text-right text-blue-600">{formatCurrency(curr.entrate_nette)}</td>
                            <td className="p-2 text-right text-gray-400">{formatCurrency(comp.entrate_nette)}</td>
                            <td className="p-2 text-center">
                              <span className={`px-2 py-0.5 rounded text-xs ${entrateVar >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {formatPercent(entrateVar)}
                              </span>
                            </td>
                            <td className="p-2 text-right text-red-600">{formatCurrency(curr.uscite)}</td>
                            <td className="p-2 text-right text-gray-400">{formatCurrency(comp.uscite)}</td>
                            <td className="p-2 text-right font-bold text-amber-600">{formatCurrency(curr.utile_netto)}</td>
                            <td className="p-2 text-right text-gray-400">{formatCurrency(comp.utile_netto)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-100 font-bold">
                      <tr>
                        <td className="p-2">TOTALE</td>
                        <td className="p-2 text-right text-blue-600">{formatCurrency(reportData.currentYear.totals.entrate_nette)}</td>
                        <td className="p-2 text-right text-gray-500">{formatCurrency(reportData.compareYear.totals.entrate_nette)}</td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs ${reportData.yoyComparison.entrate_nette >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {formatPercent(reportData.yoyComparison.entrate_nette)}
                          </span>
                        </td>
                        <td className="p-2 text-right text-red-600">{formatCurrency(reportData.currentYear.totals.uscite)}</td>
                        <td className="p-2 text-right text-gray-500">{formatCurrency(reportData.compareYear.totals.uscite)}</td>
                        <td className="p-2 text-right text-amber-600">{formatCurrency(reportData.currentYear.totals.utile_netto)}</td>
                        <td className="p-2 text-right text-gray-500">{formatCurrency(reportData.compareYear.totals.utile_netto)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
